import { supabase } from '@/integrations/supabase/client';
import { getProductCategory, ProductCategory } from '@/constants/productCategories';
import { getCanonicalProductName } from '@/services/benchmark/productNameMapping';
import { format, startOfWeek, endOfWeek, addDays, isAfter, isBefore, isSameWeek } from 'date-fns';

export interface CompetitorPrice {
  product_name: string;
  date: string;
  price: number;
  location: string;
  source: 'local_shop' | 'sunday_market' | 'supermarket' | 'ecommerce';
}

export interface ChipChipPrice {
  product_name: string;
  date: string;
  price: number;
  num_order: number;
}

export interface WeeklyPriceComparison {
  week: string;
  weekStart: Date;
  weekEnd: Date;
  isCurrentWeek: boolean;
  categories: {
    [key in ProductCategory]: ProductPriceComparison[];
  };
}

export interface ProductPriceComparison {
  product_name: string;
  chipchip_price: number;
  competitor_prices: {
    local_shops: number;
    sunday_markets: number;
    supermarkets: number;
    ecommerce: number;
    grand_total: number;
  };
  price_differences: {
    local_shops: number;
    sunday_markets: number;
    supermarkets: number;
    ecommerce: number;
    grand_total: number;
  };
}

export const priceDownService = {
  async fetchChipChipPrices(startDate: string, endDate: string): Promise<ChipChipPrice[]> {
    const { data, error } = await supabase
      .from('chipchip_sales_data')
      .select('product_name, date, price, num_order')
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async fetchCompetitorPrices(startDate: string, endDate: string): Promise<CompetitorPrice[]> {
    const [localShops, sundayMarkets, supermarkets, ecommerce] = await Promise.all([
      supabase
        .from('local_shop_prices')
        .select('product_name, date, price, location')
        .gte('date', startDate)
        .lte('date', endDate),
      supabase
        .from('sunday_market_prices')
        .select('product_name, date, price, location')
        .gte('date', startDate)
        .lte('date', endDate),
      supabase
        .from('supermarket_prices')
        .select('product_name, date, price, location')
        .gte('date', startDate)
        .lte('date', endDate),
      supabase
        .from('ecommerce_prices')
        .select('product_name, date, price, location')
        .gte('date', startDate)
        .lte('date', endDate)
    ]);

    const competitors: CompetitorPrice[] = [];

    if (localShops.data) {
      competitors.push(...localShops.data.map(item => ({ ...item, source: 'local_shop' as const })));
    }
    if (sundayMarkets.data) {
      competitors.push(...sundayMarkets.data.map(item => ({ ...item, source: 'sunday_market' as const })));
    }
    if (supermarkets.data) {
      competitors.push(...supermarkets.data.map(item => ({ ...item, source: 'supermarket' as const })));
    }
    if (ecommerce.data) {
      competitors.push(...ecommerce.data.map(item => ({ ...item, source: 'ecommerce' as const })));
    }

    return competitors;
  },

  processWeeklyPriceComparison(
    chipchipPrices: ChipChipPrice[],
    competitorPrices: CompetitorPrice[],
    startDate: Date,
    endDate: Date
  ): WeeklyPriceComparison[] {
    const weeks: WeeklyPriceComparison[] = [];
    const currentDate = new Date();

    console.log('🔍 DEBUG: processWeeklyPriceComparison called with:');
    console.log('  startDate:', startDate, 'endDate:', endDate);
    console.log('  chipchipPrices count:', chipchipPrices.length);
    console.log('  competitorPrices count:', competitorPrices.length);
    console.log('  chipchipPrices sample:', chipchipPrices.slice(0, 3));
    console.log('  competitorPrices sample:', competitorPrices.slice(0, 3));

    let currentWeekStart = startOfWeek(startDate, { weekStartsOn: 1 }); // Monday
    console.log('🗓️ DEBUG: First week start:', currentWeekStart);

    while (isBefore(currentWeekStart, endDate) || isSameWeek(currentWeekStart, endDate)) {
      const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
      const weekKey = format(currentWeekStart, 'yyyy-MM-dd');
      
      console.log(`\n📅 DEBUG: Processing week ${weekKey}`);
      console.log('  Week range:', currentWeekStart, 'to', weekEnd);
      
      // Filter data for this week
      const weekChipchipPrices = chipchipPrices.filter(price => {
        const priceDate = new Date(price.date);
        return priceDate >= currentWeekStart && priceDate <= weekEnd;
      });

      const weekCompetitorPrices = competitorPrices.filter(price => {
        const priceDate = new Date(price.date);
        return priceDate >= currentWeekStart && priceDate <= weekEnd;
      });

      console.log(`  📊 DEBUG: Week ${weekKey} filtered data:`);
      console.log('    ChipChip prices count:', weekChipchipPrices.length);
      console.log('    Competitor prices count:', weekCompetitorPrices.length);
      
      if (weekChipchipPrices.length > 0) {
        console.log('    ChipChip products:', weekChipchipPrices.map(p => p.product_name));
      }
      if (weekCompetitorPrices.length > 0) {
        console.log('    Competitor products:', weekCompetitorPrices.map(p => p.product_name));
      }

      // Get unique canonical products for this week
      const uniqueProducts = new Set([
        ...weekChipchipPrices.map(p => getCanonicalProductName(p.product_name)),
        ...weekCompetitorPrices.map(p => getCanonicalProductName(p.product_name))
      ]);

      console.log(`    🏷️ DEBUG: Unique canonical products for week ${weekKey}:`, Array.from(uniqueProducts));

      const categories = {
        HIGH_RUNNERS: [] as ProductPriceComparison[],
        MID_RUNNERS: [] as ProductPriceComparison[],
        LOW_RUNNERS: [] as ProductPriceComparison[]
      };

      uniqueProducts.forEach(canonicalProductName => {
        const chipchipData = weekChipchipPrices.filter(p => getCanonicalProductName(p.product_name) === canonicalProductName);
        const competitorData = weekCompetitorPrices.filter(p => getCanonicalProductName(p.product_name) === canonicalProductName);

        if (chipchipData.length === 0) return; // Skip if no ChipChip data

        // Calculate average ChipChip price for the week
        const avgChipchipPrice = chipchipData.reduce((sum, item) => sum + item.price, 0) / chipchipData.length;

        // Calculate competitor averages by source
        const localShopPrices = competitorData.filter(p => p.source === 'local_shop');
        const sundayMarketPrices = competitorData.filter(p => p.source === 'sunday_market');
        const supermarketPrices = competitorData.filter(p => p.source === 'supermarket');
        const ecommercePrices = competitorData.filter(p => p.source === 'ecommerce');

        const avgLocalShop = localShopPrices.length > 0 
          ? localShopPrices.reduce((sum, item) => sum + item.price, 0) / localShopPrices.length 
          : 0;
        const avgSundayMarket = sundayMarketPrices.length > 0 
          ? sundayMarketPrices.reduce((sum, item) => sum + item.price, 0) / sundayMarketPrices.length 
          : 0;
        const avgSupermarket = supermarketPrices.length > 0 
          ? supermarketPrices.reduce((sum, item) => sum + item.price, 0) / supermarketPrices.length 
          : 0;
        const avgEcommerce = ecommercePrices.length > 0 
          ? ecommercePrices.reduce((sum, item) => sum + item.price, 0) / ecommercePrices.length 
          : 0;

        // Calculate grand total (average of all competitors)
        const allCompetitorPrices = [avgLocalShop, avgSundayMarket, avgSupermarket, avgEcommerce].filter(p => p > 0);
        const grandTotal = allCompetitorPrices.length > 0 
          ? allCompetitorPrices.reduce((sum, price) => sum + price, 0) / allCompetitorPrices.length 
          : 0;

        // Calculate price differences (percentage)
        const calculateDifference = (competitorPrice: number) => {
          if (competitorPrice === 0) return 0;
          return ((avgChipchipPrice - competitorPrice) / competitorPrice) * 100;
        };

        const productComparison: ProductPriceComparison = {
          product_name: canonicalProductName,
          chipchip_price: avgChipchipPrice,
          competitor_prices: {
            local_shops: avgLocalShop,
            sunday_markets: avgSundayMarket,
            supermarkets: avgSupermarket,
            ecommerce: avgEcommerce,
            grand_total: grandTotal
          },
          price_differences: {
            local_shops: calculateDifference(avgLocalShop),
            sunday_markets: calculateDifference(avgSundayMarket),
            supermarkets: calculateDifference(avgSupermarket),
            ecommerce: calculateDifference(avgEcommerce),
            grand_total: calculateDifference(grandTotal)
          }
        };

        const category = getProductCategory(canonicalProductName);
        console.log(`      🏷️ Product "${canonicalProductName}" categorized as: ${category}`);
        categories[category].push(productComparison);
      });

      console.log(`  📋 DEBUG: Week ${weekKey} final categories:`);
      console.log('    HIGH_RUNNERS:', categories.HIGH_RUNNERS.map(p => p.product_name));
      console.log('    MID_RUNNERS:', categories.MID_RUNNERS.map(p => p.product_name));
      console.log('    LOW_RUNNERS:', categories.LOW_RUNNERS.map(p => p.product_name));

      weeks.push({
        week: `Week of ${format(currentWeekStart, 'MMM dd')} - ${format(weekEnd, 'MMM dd, yyyy')}`,
        weekStart: currentWeekStart,
        weekEnd: weekEnd,
        isCurrentWeek: isSameWeek(currentWeekStart, currentDate),
        categories
      });

      currentWeekStart = addDays(currentWeekStart, 7);
    }

    console.log(`\n✅ DEBUG: processWeeklyPriceComparison completed. Generated ${weeks.length} weeks.`);
    console.log('Week summaries:', weeks.map(w => ({
      week: w.week,
      highRunners: w.categories.HIGH_RUNNERS.length,
      midRunners: w.categories.MID_RUNNERS.length,
      lowRunners: w.categories.LOW_RUNNERS.length
    })));

    return weeks.reverse(); // Most recent weeks first
  }
};